<template>
  <Teleport to="body">
    <div class="toast-container">
      <TransitionGroup name="toast" tag="div">
        <div
          v-for="toast in toasts"
          :key="toast.id"
          class="toast-item"
          :class="[
            `toast-${toast.type}`,
            { 'toast-clickable': toast.url }
          ]"
          @click="handleToastClick(toast)"
        >
          <!-- Close Button -->
          <button
            @click.stop="removeToast(toast.id)"
            class="toast-close"
            type="button"
          >
            <i class="ki-duotone ki-cross fs-6">
              <span class="path1"></span>
              <span class="path2"></span>
            </i>
          </button>

          <!-- Icon -->
          <div class="toast-icon">
            <i 
              class="ki-duotone fs-2"
              :class="getIconClass(toast.type)"
            >
              <span class="path1"></span>
              <span class="path2"></span>
              <span v-if="toast.type === 'error'" class="path3"></span>
            </i>
          </div>

          <!-- Content -->
          <div class="toast-content">
            <h6 class="toast-title">{{ toast.title }}</h6>
            <p class="toast-message">{{ toast.message }}</p>
            <small class="toast-time">{{ formatTime(toast.showTime) }}</small>
          </div>

          <!-- Progress Bar (for auto close) -->
          <div 
            v-if="toast.autoClose" 
            class="toast-progress"
            :style="{ animationDuration: `${toast.duration}ms` }"
          ></div>
        </div>
      </TransitionGroup>
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { useToast, type ToastNotification } from '~/composables/useToast';

const { toasts, removeToast } = useToast();

// Debug: Log toasts để kiểm tra
watch(toasts, (newToasts) => {
  console.log('ToastContainer - toasts changed:', newToasts);
}, { deep: true });

onMounted(() => {
  console.log('ToastContainer mounted');
});

const testToast = () => {
  const { addToast } = useToast();
  addToast({
    id: Date.now(),
    title: 'Test Toast',
    message: 'Đây là toast test từ ToastContainer',
    createdDate: new Date().toISOString(),
    isRead: false
  }, {
    type: 'success',
    duration: 5000,
    autoClose: true
  });
};

const handleToastClick = (toast: ToastNotification) => {
  if (toast.url) {
    navigateTo(toast.url);
  }
};

const getIconClass = (type?: string) => {
  switch (type) {
    case 'success':
      return 'ki-check-circle text-success';
    case 'error':
      return 'ki-cross-circle text-danger';
    case 'warning':
      return 'ki-warning-2 text-warning';
    case 'info':
    default:
      return 'ki-information-5 text-primary';
  }
};

const formatTime = (dateString?: string) => {
  if (!dateString) return 'Vừa xong';
  
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffInSeconds < 60) {
    return 'Vừa xong';
  } else if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes} phút trước`;
  } else {
    return date.toLocaleTimeString('vi-VN', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  }
};
</script>

<style scoped>
.toast-container {
  position: fixed !important;
  top: 20px !important;
  right: 20px !important;
  z-index: 99999 !important;
  display: flex !important;
  flex-direction: column !important;
  gap: 12px !important;
  max-width: 400px !important;
  pointer-events: none !important;
}

.toast-item {
  background: white !important;
  border-radius: 8px !important;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
  border-left: 4px solid #007bff !important;
  padding: 16px !important;
  display: flex !important;
  align-items: flex-start !important;
  gap: 12px !important;
  position: relative !important;
  pointer-events: auto !important;
  transition: all 0.3s ease !important;
  overflow: hidden !important;
  min-width: 350px !important;
  margin-bottom: 8px !important;
}

.toast-item:hover {
  box-shadow: 0 12px 35px rgba(0, 0, 0, 0.2);
  transform: translateY(-2px);
}

.toast-clickable {
  cursor: pointer;
}

.toast-clickable:hover .toast-title {
  color: var(--bs-primary);
}

/* Toast Types */
.toast-success {
  border-left-color: var(--bs-success);
  background: linear-gradient(135deg, var(--bs-light-success) 0%, var(--bs-white) 100%);
}

.toast-error {
  border-left-color: var(--bs-danger);
  background: linear-gradient(135deg, var(--bs-light-danger) 0%, var(--bs-white) 100%);
}

.toast-warning {
  border-left-color: var(--bs-warning);
  background: linear-gradient(135deg, var(--bs-light-warning) 0%, var(--bs-white) 100%);
}

.toast-info {
  border-left-color: var(--bs-primary);
  background: linear-gradient(135deg, var(--bs-light-primary) 0%, var(--bs-white) 100%);
}

/* Close Button */
.toast-close {
  position: absolute;
  top: 8px;
  right: 8px;
  background: none;
  border: none;
  color: var(--bs-gray-500);
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s ease;
}

.toast-close:hover {
  background: var(--bs-gray-200);
  color: var(--bs-gray-700);
}

/* Icon */
.toast-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--bs-gray-100);
}

/* Content */
.toast-content {
  flex: 1;
  padding-right: 20px;
}

.toast-title {
  font-weight: 600;
  font-size: 14px;
  margin: 0 0 4px 0;
  color: var(--bs-gray-800);
  line-height: 1.3;
}

.toast-message {
  font-size: 13px;
  color: var(--bs-gray-700);
  margin: 0 0 4px 0;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.toast-time {
  font-size: 11px;
  color: var(--bs-gray-600);
  font-style: italic;
}

/* Progress Bar */
.toast-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  background: var(--bs-primary);
  animation: toast-progress linear forwards;
  border-radius: 0 0 8px 8px;
}

.toast-success .toast-progress {
  background: var(--bs-success);
}

.toast-error .toast-progress {
  background: var(--bs-danger);
}

.toast-warning .toast-progress {
  background: var(--bs-warning);
}

@keyframes toast-progress {
  from {
    width: 100%;
  }
  to {
    width: 0%;
  }
}

/* Transitions */
.toast-enter-active {
  transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.toast-leave-active {
  transition: all 0.3s ease-in;
}

.toast-enter-from {
  opacity: 0;
  transform: translateX(100%) scale(0.8);
}

.toast-leave-to {
  opacity: 0;
  transform: translateX(100%) scale(0.8);
}

.toast-move {
  transition: transform 0.3s ease;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .toast-item {
    background: var(--bs-dark);
    color: var(--bs-light);
  }
  
  .toast-title {
    color: var(--bs-light);
  }
  
  .toast-message {
    color: var(--bs-gray-300);
  }
  
  .toast-time {
    color: var(--bs-gray-400);
  }
}

/* Responsive */
@media (max-width: 480px) {
  .toast-container {
    right: 10px;
    left: 10px;
    max-width: none;
  }
  
  .toast-item {
    min-width: auto;
  }
}
</style> 